import { AdminPermissionClaims } from '@otw/models/user';

/**
 * Permission groups for admin users.
 * Each permission group contains a set of permissions that control access to specific features.
 */
export const DEFAULT_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  expenses: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  inventory: {
    view: true,
    view_purchase_price: true,
    view_profit: true,
    view_profit_realized: true,
    mark_sold: true,
    mark_sold_elsewhere: true,
    mark_for_transport: true,
    mark_ready_for_sale: true,
    purchase_without_pickup_address: true,
    mark_pending_sale_not_purchased: true,
    mark_title_as_delivered: true,
    mark_title_as_received: true,
    bulk_update_price: true,
    create: true,
    modify: true,
    delete: true,
  },
  graybook: {
    view: true,
    view_wholesale: true,
    create: true,
    modify: true,
    delete: true,
  },
  applications: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  financing_profiles: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  bank_profiles: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  lead_operations: {
    view: false,
    modify: false,
  },
  user_management: {
    view: true,
    create: true,
    create_admin: true,
    modify: true,
    delete: true,
  },
  lead_management: {
    view_own: true,
    view_all: true,
    view_buy_price: true,
    view_profit: true,
    view_financing_points: true,
    create: true,
    modify: true,
    delete: true,
  },
  photo_uploader: {
    view: true,
    upload: true,
  },
  transporter: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    cancel_charge: true,
  },
  dashboard_overview: {
    financial_metrics: true,
    warranty_metrics: true,
    financing_shipping_metrics: true,
    outstanding_owed_metrics: true,
    vehicles_owned_metrics: true,
    vehicles_funded_metrics: true,
    expense_summary: true,
    recent_sales: true,
    expected_vs_actual_repair: true,
    sales_overview: true,
    monthly_comparison: true,
  },
  dashboard_inventory: {
    inventory_stock_overview: true,
    inventory_metrics: true,
  },
  dashboard_sales: {
    active_deals: true,
    pending_deals: true,
    monthly_sales_performance: true,
    warranty_details: true,
  },
  dashboard_applications: {
    financing_applications: true,
  },
  action_banner: {
    view_completed_repairs: true,
    view_pickup_locations_needed: true,
    view_gate_passes_needed: true,
  },
};

/**
 * Permission groups for inventory admin users.
 */
export const INVENTORY_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  expenses: {
    view: true,
    create: true,
    modify: true, 
    delete: true,
  },
  inventory: {
    view: true,
    view_purchase_price: true,
    view_profit: true,
    view_profit_realized: false,
    mark_sold: true,
    mark_sold_elsewhere: true,
    mark_for_transport: false,
    mark_ready_for_sale: false,
    purchase_without_pickup_address: false,
    mark_pending_sale_not_purchased: false,
    mark_title_as_delivered: false,
    mark_title_as_received: false,
    bulk_update_price: true,
    create: true,
    modify: true,
    delete: true,
  },
  graybook: {
    view: true,
    view_wholesale: false,
    create: false,
    modify: false,
    delete: false,
  },
  applications: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  financing_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  bank_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  lead_operations: {
    view: false,
    modify: false,
  },
  user_management: {
    view: true,
    create: true,
    create_admin: false,
    modify: true,
    delete: true,
  },
  lead_management: {
    view_own: false,
    view_all: false,
    view_buy_price: false,
    view_profit: false,
    view_financing_points: false,
    create: false,
    modify: false,
    delete: false,
  },
  photo_uploader: {
    view: true,
    upload: true,
  },
  transporter: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    cancel_charge: false,
  },
  dashboard_overview: {
    financial_metrics: true,
    warranty_metrics: true,
    financing_shipping_metrics: true,
    outstanding_owed_metrics: true,
    vehicles_owned_metrics: true,
    vehicles_funded_metrics: true,
    expense_summary: true,
    recent_sales: true,
    expected_vs_actual_repair: true,
    sales_overview: true,
    monthly_comparison: true,
  },
  dashboard_inventory: {
    inventory_stock_overview: true,
    inventory_metrics: true,
  },
  dashboard_sales: {
    active_deals: true,
    pending_deals: true,
    monthly_sales_performance: true,
    warranty_details: true,
  },
  dashboard_applications: {
    financing_applications: true,
  },
  action_banner: {
    view_completed_repairs: true,
    view_pickup_locations_needed: true,
    view_gate_passes_needed: true,
  },
};

/**
 * Permission groups for sales users.
 */
export const SALES_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  expenses: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  inventory: {
    view: true,
    view_purchase_price: false,
    view_profit: false,
    view_profit_realized: false,
    mark_sold: true,
    mark_sold_elsewhere: true,
    mark_for_transport: false,
    mark_ready_for_sale: false,
    purchase_without_pickup_address: false,
    mark_pending_sale_not_purchased: false,
    mark_title_as_delivered: false,
    mark_title_as_received: false,
    bulk_update_price: true,
    create: true,
    modify: true,
    delete: true,
  },
  graybook: {
    view: true,
    view_wholesale: false,
    create: false,
    modify: false,
    delete: false,
  },
  applications: {
    view: true,
    create: true,
    modify: false,
    delete: false,
  },
  financing_profiles: {
    view: true,
    create: true,
    modify: false,
    delete: false,
  },
  bank_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  lead_operations: {
    view: false,
    modify: false,
  },
  user_management: {
    view: true,
    create: true,
    create_admin: false,
    modify: true,
    delete: true,
  },
  lead_management: {
    view_own: true,
    view_all: false,
    view_buy_price: false,
    view_profit: false,
    view_financing_points: false,
    create: true,
    modify: true,
    delete: true,
  },
  photo_uploader: {
    view: true,
    upload: true,
  },
  transporter: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    cancel_charge: false,
  },
  dashboard_overview: {
    financial_metrics: true,
    warranty_metrics: true,
    financing_shipping_metrics: true,
    outstanding_owed_metrics: true,
    vehicles_owned_metrics: true,
    vehicles_funded_metrics: true,
    expense_summary: true,
    recent_sales: true,
    expected_vs_actual_repair: true,
    sales_overview: true,
    monthly_comparison: true,
  },
  dashboard_inventory: {
    inventory_stock_overview: true,
    inventory_metrics: true,
  },
  dashboard_sales: {
    active_deals: true,
    pending_deals: true,
    monthly_sales_performance: true,
    warranty_details: true,
  },
  dashboard_applications: {
    financing_applications: true,
  },
  action_banner: {
    view_completed_repairs: true,
    view_pickup_locations_needed: true,
    view_gate_passes_needed: true,
  },
};

/**
 * Permission groups for shipping and repairs admin users.
 */
export const SHIPPING_REPAIRS_ADMIN_PERMISSIONS: AdminPermissionClaims = {
  expenses: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  inventory: {
    view: true,
    view_purchase_price: true,
    view_profit: true,
    view_profit_realized: false,
    mark_sold: true,
    mark_sold_elsewhere: true,
    mark_for_transport: false,
    mark_ready_for_sale: false,
    purchase_without_pickup_address: false,
    mark_pending_sale_not_purchased: false,
    mark_title_as_delivered: true,
    mark_title_as_received: true,
    bulk_update_price: true,
    create: true,
    modify: true,
    delete: true,
  },
  graybook: {
    view: true,
    view_wholesale: false,
    create: false,
    modify: false,
    delete: false,
  },
  applications: {
    view: true,
    create: false,
    modify: true,
    delete: true,
  },
  financing_profiles: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  bank_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  lead_operations: {
    view: false,
    modify: false,
  },
  user_management: {
    view: true,
    create: true,
    create_admin: false,
    modify: true,
    delete: true,
  },
  lead_management: {
    view_own: false,
    view_all: false,
    view_buy_price: false,
    view_profit: false,
    view_financing_points: false,
    create: false,
    modify: false,
    delete: false,
  },
  photo_uploader: {
    view: true,
    upload: true,
  },
  transporter: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    cancel_charge: true,
  },
  dashboard_overview: {
    financial_metrics: true,
    warranty_metrics: true,
    financing_shipping_metrics: true,
    outstanding_owed_metrics: true,
    vehicles_owned_metrics: true,
    vehicles_funded_metrics: true,
    expense_summary: true,
    recent_sales: true,
    expected_vs_actual_repair: true,
    sales_overview: true,
    monthly_comparison: true,
  },
  dashboard_inventory: {
    inventory_stock_overview: true,
    inventory_metrics: true,
  },
  dashboard_sales: {
    active_deals: true,
    pending_deals: true,
    monthly_sales_performance: true,
    warranty_details: true,
  },
  dashboard_applications: {
    financing_applications: true,
  },
  action_banner: {
    view_completed_repairs: true,
    view_pickup_locations_needed: true,
    view_gate_passes_needed: true,
  },
};

/**
 * Permission groups for shipping and repairs users.
 */
export const SHIPPING_REPAIRS_PERMISSIONS: AdminPermissionClaims = {
  expenses: {
    view: true,
    create: true,
    modify: true,
    delete: true,
  },
  inventory: {
    view: true,
    view_purchase_price: false,
    view_profit: false,
    view_profit_realized: false,
    mark_sold: true,
    mark_sold_elsewhere: true,
    mark_for_transport: false,
    mark_ready_for_sale: false,
    purchase_without_pickup_address: false,
    mark_pending_sale_not_purchased: false,
    mark_title_as_delivered: false,
    mark_title_as_received: false,
    bulk_update_price: true,
    create: true,
    modify: true,
    delete: true,
  },
  graybook: {
    view: true,
    view_wholesale: false,
    create: false,
    modify: false,
    delete: false,
  },
  applications: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  financing_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  bank_profiles: {
    view: false,
    create: false,
    modify: false,
    delete: false,
  },
  lead_operations: {
    view: false,
    modify: false,
  },
  user_management: {
    view: true,
    create: true,
    create_admin: false,
    modify: true,
    delete: true,
  },
  lead_management: {
    view_own: false,
    view_all: false,
    view_buy_price: false,
    view_profit: false,
    view_financing_points: false,
    create: false,
    modify: false,
    delete: false,
  },
  photo_uploader: {
    view: true,
    upload: true,
  },
  transporter: {
    view: true,
    create: true,
    modify: true,
    delete: true,
    cancel_charge: false,
  },
  dashboard_overview: {
    financial_metrics: false,
    warranty_metrics: false,
    financing_shipping_metrics: false,
    outstanding_owed_metrics: false,
    vehicles_owned_metrics: false,
    vehicles_funded_metrics: false,
    expense_summary: false,
    recent_sales: false,
    expected_vs_actual_repair: false,
    sales_overview: false,
    monthly_comparison: false,
  },
  dashboard_inventory: {
    inventory_stock_overview: true,
    inventory_metrics: true,
  },
  dashboard_sales: {
    active_deals: true,
    pending_deals: true,
    monthly_sales_performance: true,
    warranty_details: true,
  },
  dashboard_applications: {
    financing_applications: true,
  },
  action_banner: {
    view_completed_repairs: true,
    view_pickup_locations_needed: true,
    view_gate_passes_needed: true,
  },
};

/**
 * Default permissions for bank agent users — limited to LTV calculator access.
 */
export const DEFAULT_BANK_PERMISSIONS: AdminPermissionClaims = {
  ...createEmptyPermissions(),
  graybook: {
    view: true,
    view_wholesale: false,
    create: false,
    modify: false,
    delete: false,
  },
}

/**
 * Get the default permission template for a role
 */
export function getDefaultPermissionsForRole(role: string): AdminPermissionClaims {
  switch (role.toLowerCase()) {
    case 'admin':
    case 'babyadmin':
      return DEFAULT_ADMIN_PERMISSIONS;
    case 'inventory':
      return INVENTORY_ADMIN_PERMISSIONS;
    case 'sales':
      return SALES_ADMIN_PERMISSIONS;
    case 'shipping_repairs_admin':
      return SHIPPING_REPAIRS_ADMIN_PERMISSIONS;
    case 'shipping_repairs':
      return SHIPPING_REPAIRS_PERMISSIONS;
    case 'bankagent':
      return DEFAULT_BANK_PERMISSIONS;
    default:
      // Return empty permissions for other roles
      return createEmptyPermissions();
  }
}

/**
 * Create an empty permissions object
 */
export function createEmptyPermissions(): AdminPermissionClaims {
  return {
    expenses: { view: false, create: false, modify: false, delete: false },
    inventory: { view: false, view_purchase_price: false, view_profit: false, view_profit_realized: false, mark_sold: false, mark_sold_elsewhere: false, mark_for_transport: false, mark_ready_for_sale: false, purchase_without_pickup_address: false, mark_pending_sale_not_purchased: false, mark_title_as_delivered: false, mark_title_as_received: false, bulk_update_price: false, create: false, modify: false, delete: false },
    graybook: { view: false, view_wholesale: false, create: false, modify: false, delete: false },
    applications: { view: false, create: false, modify: false, delete: false },
    financing_profiles: { view: false, create: false, modify: false, delete: false },
    bank_profiles: { view: false, create: false, modify: false, delete: false },
    lead_operations: { view: false, modify: false },
    user_management: { view: false, create: false, create_admin: false, modify: false, delete: false },
    lead_management: { view_own: false, view_all: false, view_buy_price: false, view_profit: false, view_financing_points: false, create: false, modify: false, delete: false },
    photo_uploader: { view: false, upload: false },
    transporter: { view: false, create: false, modify: false, delete: false, cancel_charge: false },
    dashboard_overview: {
      financial_metrics: false,
      warranty_metrics: false,
      financing_shipping_metrics: false,
      outstanding_owed_metrics: false,
      vehicles_owned_metrics: false,
      vehicles_funded_metrics: false,
      expense_summary: false,
      recent_sales: false,
      expected_vs_actual_repair: false,
      sales_overview: false,
      monthly_comparison: false,
    },
    dashboard_inventory: {
      inventory_stock_overview: false,
      inventory_metrics: false,
    },
    dashboard_sales: {
      active_deals: false,
      pending_deals: false,
      monthly_sales_performance: false,
      warranty_details: false,
    },
    dashboard_applications: {
      financing_applications: false,
    },
    action_banner: {
      view_completed_repairs: false,
      view_pickup_locations_needed: false,
      view_gate_passes_needed: false,
    },
  };
}

/**
 * Get all permission groups
 */
export function getAllPermissionGroups(): string[] {
  return Object.keys(createEmptyPermissions());
}

/**
 * Get all permissions for a specific group
 */
export function getPermissionsForGroup(group: keyof AdminPermissionClaims): string[] {
  const empty = createEmptyPermissions();
  return Object.keys(empty[group]);
}

